import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Phone, Mail, Globe, MapPin, Hand, CheckCircle2 } from "lucide-react";
import { ImzLogo, BsnlLogo } from "@/components/Brand";
import { TopBar, ScreenShell, KioskButton, SectionLabel, useKiosk } from "../ui";
import { AutoFit } from "../AutoFit";
import { ConnectButton } from "../ConnectForm";

export function ContactScreen() {
  const { contact, branding, nav } = useKiosk();
  const c = contact || {};
  const rows = [
    { icon: Phone, value: c.phone, href: c.phone ? `tel:${c.phone}` : null, testId: "contact-phone" },
    { icon: MapPin, value: c.address, testId: "contact-address" },
    { icon: Mail, value: c.email, href: c.email ? `mailto:${c.email}` : null, testId: "contact-email" },
    { icon: Globe, value: c.website, href: c.website, testId: "contact-website" },
  ].filter((r) => r.value);

  return (
    <ScreenShell>
      <TopBar title="Contact Us" onBack={() => nav("solutions")} />
      <div className="min-h-0 flex-1 overflow-hidden px-5 sm:px-8 py-8" data-testid="kiosk-contact-qr-container">
        <AutoFit>
        <div className="mx-auto max-w-lg">
          <SectionLabel>{c.company_name || "IMZ Corporate"}</SectionLabel>
          <h1 className="mt-2 font-head text-3xl sm:text-4xl font-extrabold text-white">{c.headline || "Let's Build a Safer Tomorrow"}</h1>
          <p className="mt-2 text-sm sm:text-base text-slate-400">{c.message}</p>

          <ConnectButton className="mt-6 w-full" />

          <div className="mt-8 space-y-3">
            {rows.map((r, i) => {
              const Inner = (
                <div className="flex items-center gap-4 rounded-2xl glass glow-border-hover px-5 py-4">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-cyan-400/40 text-cyan-300">
                    <r.icon className="h-5 w-5" />
                  </div>
                  <span className="text-base text-slate-100 break-all">{r.value}</span>
                </div>
              );
              return r.href ? (
                <a key={i} href={r.href} target="_blank" rel="noreferrer" data-testid={r.testId}>{Inner}</a>
              ) : (
                <div key={i} data-testid={r.testId}>{Inner}</div>
              );
            })}
          </div>

          {c.qr_url && (
            <div className="mt-8 flex flex-col items-center gap-3">
              <div className="rounded-3xl bg-white p-4" data-testid="kiosk-contact-qr">
                <QRCodeSVG value={c.qr_url} size={168} bgColor="#ffffff" fgColor="#050b18" level="M" />
              </div>
              <span className="font-tele text-xs uppercase tracking-[0.25em] text-slate-500">Scan to visit our website</span>
            </div>
          )}

          {c.is_sample && (
            <p className="mt-6 text-center text-xs text-slate-600">Sample contact details — configurable from the admin panel.</p>
          )}
        </div>
        </AutoFit>
      </div>
    </ScreenShell>
  );
}

export function ThankYouScreen() {
  const { branding, goHome, startExperience, nav } = useKiosk();
  const t = branding?.thankyou || {};
  const [count, setCount] = useState(12);

  useEffect(() => {
    const id = setInterval(() => setCount((c) => (c <= 1 ? (clearInterval(id), goHome(), 0) : c - 1)), 1000);
    return () => clearInterval(id);
  }, [goHome]);

  return (
    <motion.div
      data-testid="kiosk-thank-you-view"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
      className="relative flex h-full flex-col items-center justify-center overflow-hidden px-6 py-10 text-center kiosk-radial"
    >
      <div className="absolute inset-x-0 top-0 flex items-center justify-between px-6 py-5">
        <BsnlLogo />
        <ImzLogo />
      </div>

      <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 160 }}>
        <div className="relative flex h-28 w-28 items-center justify-center rounded-full border-2 border-cyan-400/50">
          <span className="pulse-ring absolute inset-0 rounded-full border border-cyan-400/50" />
          <CheckCircle2 className="h-14 w-14 text-cyan-300" />
        </div>
      </motion.div>

      <h1 className="mt-8 font-head text-4xl sm:text-5xl font-extrabold text-white">{t.title || "Thank You!"}</h1>
      <p className="mt-4 max-w-md text-base sm:text-lg text-slate-300">{t.message || "We're glad you explored our solutions."}</p>
      <p className="mt-2 max-w-md text-sm text-slate-400">{t.submessage || "Stay connected for a safer, smarter tomorrow."}</p>

      <KioskButton testId="kiosk-thankyou-home" className="mt-10" onClick={() => { startExperience(); nav("solutions"); }}>
        {t.button_label || "Back to Home"}
      </KioskButton>
      <span className="mt-4 font-tele text-xs text-slate-500">Returning to welcome in {count}s</span>
    </motion.div>
  );
}

export function ClosingScreen() {
  const { branding, startExperience, nav } = useKiosk();
  const cl = branding?.closing || {};
  const words = cl.words || ["Secure", "Analyze", "Optimize"];

  return (
    <motion.div
      data-testid="kiosk-closing-attract-loop"
      onClick={() => { startExperience(); nav("solutions"); }}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
      className="relative flex h-full cursor-pointer flex-col items-center justify-between overflow-hidden px-6 py-8 text-center"
    >
      {cl.image_url && (
        <img src={cl.image_url} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050b18]/80 via-[#050b18]/60 to-[#050b18]" />

      <div className="z-10 flex w-full items-center justify-between">
        <BsnlLogo />
        <ImzLogo />
      </div>

      <div className="z-10 flex flex-col items-center gap-4">
        <h1 className="font-head text-4xl sm:text-6xl font-extrabold text-white">
          {cl.headline || "Securing India's Assets"}
        </h1>
        <div className="flex items-center gap-3 font-head text-lg font-semibold text-slate-200">
          {words.map((w, i) => (
            <span key={w} className="flex items-center gap-3">
              {i > 0 && <span className="text-cyan-400/60">|</span>}{w}
            </span>
          ))}
        </div>
      </div>

      <div className="z-10 flex flex-col items-center gap-2">
        <motion.span animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="text-cyan-300">
          <Hand className="h-8 w-8" />
        </motion.span>
        <span className="font-tele text-xs uppercase tracking-[0.3em] text-slate-400">Tap anywhere to continue</span>
        <div className="mt-2 flex items-center gap-4 text-[10px] font-tele uppercase tracking-widest text-slate-500">
          <span>Presented by {cl.presented_by || "IMZ Corporate Pvt. Ltd."}</span>
          <span>Powered by {cl.powered_by || "BSNL"}</span>
        </div>
      </div>
    </motion.div>
  );
}

export function IdleOverlay({ seconds, total, onContinue }) {
  const pct = Math.max(0, Math.min(1, seconds / total));
  const circ = 2 * Math.PI * 52;
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-[#050b18]/92 backdrop-blur-md px-6 text-center"
      onClick={onContinue}
      data-testid="kiosk-idle-modal"
    >
      <div className="relative flex h-40 w-40 items-center justify-center">
        <svg className="absolute inset-0 -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(0,229,255,0.12)" strokeWidth="6" />
          <circle cx="60" cy="60" r="52" fill="none" stroke="#00e5ff" strokeWidth="6" strokeLinecap="round"
            strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)} />
        </svg>
        <span className="font-head text-5xl font-extrabold text-white" data-testid="kiosk-idle-count">
          {String(Math.max(0, seconds)).padStart(2, "0")}
        </span>
      </div>
      <p className="mt-6 text-base text-slate-300">Screen will return to home in {seconds} seconds</p>
      <KioskButton testId="kiosk-idle-continue" className="mt-6" onClick={(e) => { e.stopPropagation(); onContinue(); }}>
        <Hand className="h-5 w-5" /> I'm still here
      </KioskButton>
    </motion.div>
  );
}
